import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";

import { Column, ColumnType } from "../../components/datatable/interfaces";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { endpoints } from "../api/ApiEndpoints";
import { User } from "./User";
import { Option } from "../../components/datatable/interfaces";
import { Role } from "./Role";

const ProjectUsersFilterModel = types.model({
  projectId: types.number,
  userRoleOptions: types.array(types.frozen<Option>()),
});

export const ProjectUsersStorageModel = types
  .compose(ProjectUsersFilterModel, DataGridStorageModel)
  .named("ProjectUsersStorageModel")
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.user-name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          containsLink: true,
          flex: 2,
        },
        {
          key: "role",
          name: "general.users.list-header.role",
          type: ColumnType.SingleSelect,
          valueOptions: self.userRoleOptions,
          sortable: true,
          filterable: true,
        },
        {
          key: "openJobs",
          name: "general.users.list-header.open-jobs",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "totalJobs",
          name: "general.users.list-header.total-jobs",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "email",
          name: "general.users.list-header.email",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "phone",
          name: "general.users.list-header.phone-number",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: any) {
        return new User(item);
      },

      mapResponse(response: any) {
        self.data = {
          totalCount: response.totalCount,
          items: response.results.map(self.mapItem),
        };
        self.gridColumns = self.tableColumns;
      },

      setUserRoleOptions(userRoles: Role[]) {
        const userRolesOptions = userRoles.map((role) => {
          return {
            label: role.translationKey,
            value: role.id.toString(),
          };
        });
        self.userRoleOptions = cast(userRolesOptions);
      },

      setProjectId(projectId: number) {
        self.projectId = projectId;
      },

      addUsersToProject: flow(function* addUsersToProject(projectId, usersIds) {
        const endpoint = endpoints.get("users_available_to_join_project_team");
        endpoint.routeParams = [projectId];
        const response = yield getEnv(self).api.put(endpoint, usersIds);
        yield self.load();
        return response;
      }),

      removeUsersFromProject: flow(function* removeUsersFromProject(usersIds) {
        const endpoint = endpoints.get("users_available_to_join_project_team");
        endpoint.routeParams = [self.projectId];
        const response = yield getEnv(self).api.delete(endpoint, usersIds);
        self.deselectAll();
        yield self.load();
        return response;
      }),

      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
      },
    };
  })

  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("project_team_members_all");
      endpoint.routeParams = [self.projectId];
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },

    get canLoadItems() {
      return !!self.projectId;
    },
  }));

export type ProjectUsersStorage = Instance<typeof ProjectUsersStorageModel>;
